import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { 
  ChevronDown, 
  ChevronUp,
  Box,
  Filter,
  Gauge,
  Activity,
  RotateCw,
  AlertTriangle,
  Thermometer,
  Timer,
  Waves,
  Zap,
  Beaker,
  Droplets
} from 'lucide-react';
import { ProcessFlowDiagram } from './ProcessFlowDiagram';
import { AssetGauge } from './AssetGauge';
import { Asset } from '../types';

export const UltraFiltrationStage: React.FC = () => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<string | null>(null);

  useEffect(() => {
    const initialAssets: Asset[] = [
      {
        id: 'uf-feed-pump1',
        name: 'UF Feed Pump',
        type: 'Centrifugal Pump',
        description: 'High-pressure feed pump for UF system',
        manufacturer: 'Grundfos',
        model: 'CR 95-4',
        installationDate: '2023-07-01',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Flow Rate',
            value: 180,
            unit: 'm³/h',
            normalRange: { min: 150, max: 200 },
            criticalRange: { min: 130, max: 220 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 180 + Math.random() * 10 - 5
            }))
          },
          {
            name: 'Pressure',
            value: 4.2,
            unit: 'bar',
            normalRange: { min: 3.8, max: 4.5 },
            criticalRange: { min: 3.5, max: 5.0 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 4.2 + Math.random() * 0.2 - 0.1
            }))
          },
          {
            name: 'Power Draw',
            value: 45,
            unit: 'kW',
            normalRange: { min: 40, max: 50 },
            criticalRange: { min: 35, max: 55 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 45 + Math.random() * 2 - 1
            }))
          }
        ]
      },
      {
        id: 'uf-module1',
        name: 'UF Module Bank 1',
        type: 'Membrane Module',
        description: 'Primary ultrafiltration membrane bank',
        manufacturer: 'DOW',
        model: 'IntegraFlux™ SFP-2880XP',
        installationDate: '2023-07-05',
        maintenanceSchedule: 'Monthly',
        status: 'operational',
        metrics: [
          {
            name: 'TMP',
            value: 0.8,
            unit: 'bar',
            normalRange: { min: 0.5, max: 1.2 },
            criticalRange: { min: 0.3, max: 1.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.8 + Math.random() * 0.1 - 0.05
            }))
          },
          {
            name: 'Permeate Flow',
            value: 165,
            unit: 'm³/h',
            normalRange: { min: 150, max: 180 },
            criticalRange: { min: 140, max: 190 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 165 + Math.random() * 6 - 3
            }))
          },
          {
            name: 'Turbidity',
            value: 0.08,
            unit: 'NTU',
            normalRange: { min: 0, max: 0.15 },
            criticalRange: { min: 0, max: 0.2 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.08 + Math.random() * 0.02 - 0.01
            }))
          }
        ]
      },
      {
        id: 'cip-system',
        name: 'CIP System',
        type: 'Cleaning System',
        description: 'Clean-in-place system for UF modules',
        manufacturer: 'Alfa Laval',
        model: 'CIP-35',
        installationDate: '2023-07-10',
        maintenanceSchedule: 'Quarterly',
        status: 'operational',
        metrics: [
          {
            name: 'Solution Temperature',
            value: 35,
            unit: '°C',
            normalRange: { min: 30, max: 40 },
            criticalRange: { min: 25, max: 45 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 35 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Chemical Concentration',
            value: 2.5,
            unit: '%',
            normalRange: { min: 2.0, max: 3.0 },
            criticalRange: { min: 1.5, max: 3.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.5 + Math.random() * 0.2 - 0.1
            }))
          },
          {
            name: 'Circulation Flow',
            value: 45,
            unit: 'm³/h',
            normalRange: { min: 40, max: 50 },
            criticalRange: { min: 35, max: 55 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 45 + Math.random() * 2 - 1
            }))
          }
        ]
      },
      {
        id: 'backwash-system',
        name: 'Backwash System',
        type: 'Membrane Cleaning',
        description: 'Automated backwash system',
        manufacturer: 'Hydranautics',
        model: 'AutoFlush-2000',
        installationDate: '2023-07-15',
        maintenanceSchedule: 'Monthly',
        status: 'warning',
        metrics: [
          {
            name: 'Backwash Flow',
            value: 220,
            unit: 'm³/h',
            normalRange: { min: 200, max: 250 },
            criticalRange: { min: 180, max: 270 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 220 + Math.random() * 10 - 5
            }))
          },
          {
            name: 'Duration',
            value: 45,
            unit: 'sec',
            normalRange: { min: 40, max: 50 },
            criticalRange: { min: 35, max: 55 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 45 + Math.random() * 2 - 1
            }))
          },
          {
            name: 'Pressure',
            value: 2.8,
            unit: 'bar',
            normalRange: { min: 2.5, max: 3.0 },
            criticalRange: { min: 2.0, max: 3.5 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 2.8 + Math.random() * 0.1 - 0.05
            }))
          }
        ]
      },
      {
        id: 'permeate-tank',
        name: 'Permeate Tank',
        type: 'Storage Tank',
        description: 'Filtered water storage tank',
        manufacturer: 'Custom Built',
        model: 'ST-500',
        installationDate: '2023-07-20',
        maintenanceSchedule: 'Semi-annual',
        status: 'operational',
        metrics: [
          {
            name: 'Level',
            value: 75,
            unit: '%',
            normalRange: { min: 40, max: 90 },
            criticalRange: { min: 20, max: 95 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 75 + Math.random() * 4 - 2
            }))
          },
          {
            name: 'Turbidity',
            value: 0.05,
            unit: 'NTU',
            normalRange: { min: 0, max: 0.1 },
            criticalRange: { min: 0, max: 0.15 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 0.05 + Math.random() * 0.01 - 0.005
            }))
          },
          {
            name: 'Temperature',
            value: 20,
            unit: '°C',
            normalRange: { min: 15, max: 25 },
            criticalRange: { min: 10, max: 30 },
            history: Array(20).fill(0).map((_, i) => ({
              timestamp: Date.now() - (19 - i) * 60000,
              value: 20 + Math.random() * 1 - 0.5
            }))
          }
        ]
      }
    ];

    setAssets(initialAssets);

    const interval = setInterval(() => {
      setAssets(prevAssets => prevAssets.map(asset => ({
        ...asset,
        metrics: asset.metrics.map(metric => {
          const newValue = metric.value + (Math.random() * 2 - 1) * (metric.value * 0.02);
          return {
            ...metric,
            value: newValue,
            history: [
              ...metric.history.slice(1),
              {
                timestamp: Date.now(),
                value: newValue
              }
            ]
          };
        })
      })));
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const getAssetIcon = (assetId: string) => {
    if (assetId.includes('pump')) return <Box className="h-6 w-6" />;
    if (assetId.includes('module')) return <Filter className="h-6 w-6" />;
    if (assetId.includes('cip')) return <Beaker className="h-6 w-6" />;
    if (assetId.includes('backwash')) return <Droplets className="h-6 w-6" />;
    if (assetId.includes('tank')) return <Box className="h-6 w-6" />;
    return <Activity className="h-6 w-6" />;
  };

  const getMetricIcon = (metricName: string) => {
    if (metricName.includes('Temperature')) return <Thermometer className="h-4 w-4" />;
    if (metricName.includes('Flow')) return <Waves className="h-4 w-4" />;
    if (metricName.includes('Time') || metricName.includes('Duration')) return <Timer className="h-4 w-4" />;
    if (metricName.includes('Power')) return <Zap className="h-4 w-4" />;
    if (metricName.includes('Level')) return <Activity className="h-4 w-4" />;
    if (metricName.includes('Pressure') || metricName.includes('TMP')) return <Gauge className="h-4 w-4" />;
    if (metricName.includes('Chemical')) return <Beaker className="h-4 w-4" />;
    if (metricName.includes('Turbidity')) return <Filter className="h-4 w-4" />;
    return <Activity className="h-4 w-4" />;
  };

  const getStatusColor = (status: Asset['status']) => {
    switch (status) {
      case 'operational':
        return 'bg-green-100 text-green-800';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800';
      case 'error':
        return 'bg-red-100 text-red-800';
    }
  };

  const isMetricInRange = (value: number, metric: Asset['metrics'][0]) => {
    if (!metric.normalRange) return true;
    return value >= metric.normalRange.min && value <= metric.normalRange.max;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden mt-6">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 bg-purple-600 text-white flex items-center justify-between hover:bg-purple-700 transition-colors"
      >
        <div className="flex items-center space-x-2">
          <Filter className="h-6 w-6" />
          <h2 className="text-lg font-semibold">Stage 3 - Ultra-Filtration</h2>
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
      </button>

      {isExpanded && (
        <div className="p-4">
          <div className="mb-6">
            <ProcessFlowDiagram assets={assets} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            {assets.map(asset => 
              asset.metrics.slice(0, 1).map(metric => (
                <div key={`${asset.id}-${metric.name}`} className="bg-white rounded-lg shadow p-4">
                  <AssetGauge
                    value={metric.value}
                    min={metric.normalRange?.min || 0}
                    max={metric.normalRange?.max || 100}
                    unit={metric.unit}
                    title={`${asset.name} - ${metric.name}`}
                  />
                </div>
              ))
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {assets.map(asset => (
              <div
                key={asset.id}
                onClick={() => setSelectedAsset(asset.id === selectedAsset ? null : asset.id)}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  asset.id === selectedAsset
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-gray-200 hover:border-purple-300'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    {getAssetIcon(asset.id)}
                    <div>
                      <h3 className="font-medium">{asset.name}</h3>
                      <p className="text-sm text-gray-500">{asset.type}</p>
                    </div>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(asset.status)}`}>
                    {asset.status}
                  </span>
                </div>
                <div className="space-y-2">
                  {asset.metrics.map(metric => (
                    <div key={metric.name} className="flex justify-between items-baseline">
                      <div className="flex items-center space-x-2">
                        {getMetricIcon(metric.name)}
                        <span className="text-sm text-gray-500">{metric.name}</span>
                      </div>
                      <span className={`text-lg font-semibold ${
                        isMetricInRange(metric.value, metric) 
                          ? 'text-gray-900' 
                          : 'text-red-600'
                      }`}>
                        {metric.value.toFixed(1)} {metric.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};